import React, { useEffect } from 'react';
import { Routes, Route, useParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { AppRouter } from './AppRouter';
import { TemplateApp } from './TemplateApp';

interface OrganizationRouterProps {
  children?: React.ReactNode;
}

export const OrganizationRouter: React.FC<OrganizationRouterProps> = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const { isAuthenticated, isLoading, getCurrentOrganizationId, switchWorkspace } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && orgId) {
      const currentOrgId = getCurrentOrganizationId();
      
      // If the URL org ID doesn't match the current workspace, switch workspace
      if (currentOrgId !== orgId) {
        console.log(`Switching workspace from ${currentOrgId} to ${orgId}`);
        switchWorkspace(orgId).catch((error) => {
          console.error('Failed to switch workspace:', error);
          // If switching fails, redirect to the current workspace
          navigate(`/${currentOrgId}${window.location.pathname.replace(`/${orgId}`, '')}`);
        });
      }
    }
  }, [orgId, isAuthenticated, getCurrentOrganizationId, switchWorkspace, navigate]);

  // Show loading spinner while checking authentication or switching workspace
  if (isLoading) {
    return (
      <div className="min-h-screen bg-light-bg-primary dark:bg-dark-bg-primary flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
          <p className="text-light-text-secondary dark:text-dark-text-secondary">
            Loading organization...
          </p>
        </div>
      </div>
    );
  }

  // If no org ID in URL, redirect to current organization
  if (!orgId && isAuthenticated) {
    const currentOrgId = getCurrentOrganizationId();
    navigate(`/${currentOrgId}${window.location.pathname}`);
    return null;
  }

  return (
    <Routes>
      <Route path="/templates/*" element={<TemplateApp />} />
      <Route path="/*" element={<AppRouter />} />
    </Routes>
  );
};